
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import {
    onAuthStateChanged,
    User,
    signInWithEmailAndPassword,
    signOut,
    createUserWithEmailAndPassword
} from "firebase/auth";
import { auth } from "@/config/firebase";
import { toast } from "sonner";
import { FreelanceInfo } from "@shared/types";

interface UserContextValue {
    currentUser: User | null;
    userProfile: FreelanceInfo | null;
    isLoading: boolean;
    login: (email: string, pass: string) => Promise<any>;
    logout: () => Promise<void>;
    signup: (email: string, pass: string) => Promise<any>;
    updateProfile: (data: Partial<FreelanceInfo>) => Promise<void>;
    fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [userProfile, setUserProfile] = useState<FreelanceInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const isLoadingProfile = useRef(false);

    // Memoize fetchWithAuth to prevent recreation on every render
    const fetchWithAuth = useCallback(async (url: string, options: RequestInit = {}) => {
        try {
            const token = await auth.currentUser?.getIdToken();
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                ...(options.headers as Record<string, string>)
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            const apiUrl = url.startsWith('/api') ? url : `/api${url}`;
            const response = await fetch(apiUrl, { ...options, headers });

            // Handle 401 Unauthorized - redirect to login
            if (response.status === 401) {
                console.warn('Sesión expirada o no autorizada');
                await signOut(auth);
                return response;
            }

            return response;
        } catch (error) {
            console.error('Error en fetchWithAuth:', error);
            throw error;
        }
    }, []);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setCurrentUser(firebaseUser);

            if (firebaseUser && !isLoadingProfile.current) {
                isLoadingProfile.current = true;
                try {
                    // Force token refresh to get latest custom claims
                    await firebaseUser.getIdToken(true);

                    const res = await fetchWithAuth("/perfil");
                    if (res.ok) {
                        setUserProfile(await res.json());
                    } else if (res.status === 404) {
                        setUserProfile({ id: firebaseUser.uid, email: firebaseUser.email || '' });
                    } else {
                        throw new Error('Fallo al cargar el perfil del usuario.');
                    }
                } catch (error) {
                    console.error('Error cargando perfil:', error);
                    toast.error("Error de sesión", { description: (error as Error).message });
                    setUserProfile(null);
                } finally {
                    isLoadingProfile.current = false;
                }
            } else if (!firebaseUser) {
                setUserProfile(null);
                isLoadingProfile.current = false;
            }

            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [fetchWithAuth]);

    const login = (email: string, pass: string) => signInWithEmailAndPassword(auth, email, pass);

    const logout = async () => {
        await signOut(auth);
    };

    const signup = (email: string, pass: string) => createUserWithEmailAndPassword(auth, email, pass);

    const updateProfile = useCallback(async (data: Partial<FreelanceInfo>) => {
        if (!currentUser) throw new Error("Usuario no autenticado.");
        
        const promise = fetchWithAuth("/perfil", {
            method: 'PUT',
            body: JSON.stringify(data),
        }).then(async (res) => {
            if (!res.ok) throw new Error("No se pudo actualizar el perfil.");
            const updatedUser = await res.json();
            setUserProfile(prev => ({ ...(prev || { id: currentUser.uid, email: currentUser.email! }), ...updatedUser }));
            return updatedUser;
        });

        toast.promise(promise, {
            loading: 'Actualizando perfil...',
            success: 'Perfil guardado con éxito.',
            error: 'Error al guardar el perfil.',
        });

    }, [currentUser, fetchWithAuth]);

    const value = {
        currentUser,
        userProfile,
        isLoading,
        login,
        logout,
        signup,
        updateProfile,
        fetchWithAuth,
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error("useUser must be used within a UserProvider");
    }
    return context;
};
